import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import InputWithValidation from "../../../common/components/InputWithValidation";
import Textarea from "@atlaskit/textarea";
import { Checkbox } from "@atlaskit/checkbox";
import { FlexRow } from "../../../common/style";
import Textfield from "@atlaskit/textfield";
import Select from "@atlaskit/select";
import AceEditor from "react-ace";
import "ace-builds/webpack-resolver";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-dawn";
import { clone } from "../../../common/utils/helper";
import { modifySelectedCred } from "../../../redux/actions/credActions";
import {
  ALGORITHMS,
  DEFAULT_CLAIMS_STRING,
  DEFAULT_FORM_STATE,
} from "./JwtConstants";
import PrivateKeyType from "./PrivateKeyType";
import { getAceEditorTheme } from "../../../common/utils/aceEditorTheme";

const JwtPage = () => {
  const dispatch = useDispatch();
  const [selectedAlgorithm, setSelectedAlgorithm] = useState(ALGORITHMS[0]);
  const [form, setForm] = useState(DEFAULT_FORM_STATE);
  const selectedCred = useSelector((state) => state.auth.selectedCred);

  useEffect(() => {
    setForm((prevForm) => ({
      ...prevForm,
      ...toFormState(selectedCred),
    }));
  }, [selectedCred]);

  useEffect(() => {
    if (form.algorithm) {
      setSelectedAlgorithm(ALGORITHMS.find((v) => v.value === form.algorithm));
    }
  }, [form]);

  const toFormState = (credential) => {
    return {
      claims: credential.data?.claims
        ? credential.data.claims
        : DEFAULT_CLAIMS_STRING,
      name: credential.name ? credential.name : "",
      description: credential.description ? credential.description : "",
      secret: credential.data?.secret?.value
        ? credential.data.secret.value
        : "",
      isBase64Secret: credential.data?.secret?.isBase64 ? true : false,
      headerPrefix: credential.data?.headerPrefix
        ? credential.data.headerPrefix
        : "",
      algorithm: credential.data?.algorithm ? credential.data.algorithm : "",
      useAsVariable: credential.data?.useAsVariable ? true : false,
    };
  };

  const dispatchFormAsUnsaved = (formToDispatch) => {
    const credentials = clone(selectedCred);
    const data = credentials.data ? credentials.data : {};
    data["claims"] = formToDispatch.claims;
    data["secret"] = {
      value: formToDispatch.secret,
      isBase64: formToDispatch.isBase64Secret,
    };
    data["headerPrefix"] = formToDispatch.headerPrefix;
    data["algorithm"] = formToDispatch.algorithm;
    data["useAsVariable"] = formToDispatch.useAsVariable;

    credentials["data"] = data;
    credentials["name"] = formToDispatch.name;
    credentials["description"] = formToDispatch.description;

    dispatch(modifySelectedCred(credentials));
  };

  const onFormChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;

    const newForm = { ...form, [key]: value };
    setForm(newForm);
    dispatchFormAsUnsaved(newForm);
  };

  const onFormChangeAlgorithm = (e) => {
    const newForm = { ...form, algorithm: e.value };
    setForm(newForm);
    dispatchFormAsUnsaved(newForm);
  };

  const onFormChangeClaims = (e) => {
    const newForm = { ...form, claims: e };
    setForm(newForm);
    dispatchFormAsUnsaved(newForm);
  };

  const onFormChangeIsBase64Secret = (e) => {
    //TODO FIX THIS
    const newForm = { ...form, isBase64Secret: !form.isBase64Secret };
    setForm(newForm);
    dispatchFormAsUnsaved(newForm);
  };
  const onFormChangeUseAsVariable = (e) => {
    //TODO FIX THIS
    const newForm = { ...form, useAsVariable: !form.useAsVariable };
    setForm(newForm);
    dispatchFormAsUnsaved(newForm);
  };
  return (
    <>
      <h4>Setup JWT: </h4>
      <InputWithValidation
        fieldTitle={"Name"}
        fieldName={"name"}
        onChange={onFormChange}
        inputValue={form.name ? form.name : ""}
        maxChar={70}
        minChar={2}
        type="textfield"
      />
      <InputWithValidation
        fieldTitle={"Description"}
        fieldName={"description"}
        onChange={onFormChange}
        inputValue={form.description ? form.description : ""}
        maxChar={500}
        type="textarea"
        rows={5}
      />
      <br />
      <label htmlFor="algoithm">Algorithm</label>
      <Select
        options={ALGORITHMS}
        value={selectedAlgorithm}
        spacing="compact"
        name="algorithm"
        onChange={onFormChangeAlgorithm}
      />

      <label htmlFor="secret">Secret/Private key</label>
      <Textarea
        name="secret"
        value={form.secret}
        isCompact
        onChange={onFormChange}
        rows={5}
      />
      <PrivateKeyType privateKey={form.secret} />
      <FlexRow>
        <Checkbox
          name="isBase64Secret"
          isChecked={form.isBase64Secret ? true : false}
          className="credentials-base64-checkbox"
          onChange={onFormChangeIsBase64Secret}
        />
        <span className="credentials-base64-label">
          Encode secret with Base64
        </span>
      </FlexRow>
      <FlexRow>
        <i>
          Check this box if you want to encode the secret using Base64 before
          signing the JWT. Recommended for binary data or non-ASCII characters.
        </i>
      </FlexRow>
      <br />
      <label htmlFor="claims">Claims</label>
      <AceEditor
        className="credentials-jwt-editor"
        mode="json"
        theme={getAceEditorTheme()}
        onChange={onFormChangeClaims}
        name="body"
        editorProps={{ $blockScrolling: false }}
        value={form?.claims}
      />
      <FlexRow>
        <i>
          Enter your custom JWT claims, utilize
          &#123;&#123;variables&#125;&#125; for dynamic values, and leave out
          "exp" and "iat" to have them autogenerated.
          &#123;&#123;currentTimeSeconds&#125;&#125; = Now in seconds,
          &#123;&#123;plus currentTimeSeconds 1200&#125;&#125; = Now in secods +
          1200 seconds (20 minutes).
        </i>
      </FlexRow>
      <br />

      <FlexRow>
        <span className="credentials-header-label">Header prefix</span>
        <Textfield
          value={form.headerPrefix}
          onChange={onFormChange}
          name="headerPrefix"
        />
      </FlexRow>
      <br />
      <FlexRow>
        <Checkbox
          name="useAsVariable"
          isChecked={form.useAsVariable ? true : false}
          className="credentials-base64-checkbox"
          onChange={onFormChangeUseAsVariable}
        />
        <span className="credentials-base64-label">
          Use JWT as scope variable instead of authorization header
        </span>
      </FlexRow>
      <FlexRow>
        <i>
          Check this box if you want to use this JWT as a variable in the scope.
          By doing so, you can access and utilize the JWT's information using
          Mustache syntax &#123;&#123; JWT &#125;&#125;. (set the header prefix
          to an empty string to remove that text)
        </i>
      </FlexRow>
    </>
  );
};
export default JwtPage;
